import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  Menu,
  MenuItem,
  Icon,
  Tooltip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Box,
  useMediaQuery
} from 'investira.react.components';
import { dates, formats, validators } from 'investira.sdk';
import { HighchartsReact } from 'investira.react.charts';
import { CONSOLIDACAO_TIPO } from '@investirapri/financa.js/lib/enums';
import { PessoaImage } from '../../../components/molecules';
import services from '../../../services';
import {
  ATIVO_PATRIMONIO_METRIC_TYPE,
  getAtivoPatrimonioCotistasData,
  getAtivoPatrimonioCurrentMetricInfo,
  getAtivoPatrimonioDescription,
  getAtivoPatrimonioInitialOption,
  getAtivoPatrimonioOptions,
  getAtivoPatrimonioPlData
} from './ativoPatrimonio.helpers';
import { CardHeader } from '@mui/material';
import utils from '../../../utils';

const wSeriesDashStyles = ['Solid', 'ShortDash', 'ShortDot', 'ShortDashDot'];

/** Carrega e apresenta o patrimônio de uma lista ordenada de pessoas. */
const AtivoPatrimonio = memo(props => {
  const { pessoaIds = [], dataHoje = new Date() } = props;

  const wDataFim = dates.toSqlDate(dataHoje);
  const wDataInicio = dates.toSqlDate(dates.addYears(wDataFim, -10));

  const requestCycleRef = useRef(0);
  const [anchorElConsolidacao, setAnchorElConsolidacao] = useState(null);
  const [anchorElMetrica, setAnchorElMetrica] = useState(null);
  const [pessoasPorId, setPessoasPorId] = useState({});
  const [seriesPorId, setSeriesPorId] = useState({});
  const [consolidacoes, setConsolidacoes] = useState([]);
  const [consolidacaoAtual, setConsolidacaoAtual] = useState(null);
  const [metricaAtual, setMetricaAtual] = useState(ATIVO_PATRIMONIO_METRIC_TYPE.pl);
  const xPessoaIds = useMemo(() => pessoaIds.filter(Boolean), [pessoaIds]);
  const xPessoaIdsKey = xPessoaIds.join(',');
  const isUpSm = useMediaQuery(pTheme => pTheme.breakpoints.up('sm'));
  const isUpMd = useMediaQuery(pTheme => pTheme.breakpoints.up('md'));

  useEffect(() => {
    const xCycle = requestCycleRef.current + 1;
    requestCycleRef.current = xCycle;
    setPessoasPorId({});
    setSeriesPorId(
      xPessoaIds.reduce(
        (pResultado, pId) => ({
          ...pResultado,
          [pId]: { data: [], cotistasData: [], historicoData: [], loading: true }
        }),
        {}
      )
    );
    setConsolidacoes([]);
    setConsolidacaoAtual(null);

    if (!xPessoaIds.length) return undefined;

    // const xDataFim = dates.toSqlDate(dataHoje);
    // const xDataInicio = dates.toSqlDate(dates.addYears(xDataFim, -10));
    const xOpcoesPorId = {};
    let xPendentes = xPessoaIds.length;

    xPessoaIds.forEach(xId => {
      services.pessoas.list({ pessoa_id: xId }, rRes => {
        if (requestCycleRef.current !== xCycle) return;
        setPessoasPorId(pAnterior => ({ ...pAnterior, [xId]: rRes.data?.[0] || {} }));
      });
      services.pessoas.pl(
        {
          data: null,
          data_inicio: wDataInicio,
          data_fim: wDataFim,
          sort: 'data ASC',
          consolidacao_tipo: null,
          pessoa_id: xId
        },
        rRes => {
          if (requestCycleRef.current !== xCycle) return;
          xOpcoesPorId[xId] = getAtivoPatrimonioOptions(rRes.data);
        },
        () => {
          if (requestCycleRef.current !== xCycle) return;
          xOpcoesPorId[xId] = [];
        },
        () => {
          if (requestCycleRef.current !== xCycle) return;
          xPendentes -= 1;

          if (xPendentes === 0) {
            const xOpcoes = Array.from(new Set(xPessoaIds.flatMap(xId => xOpcoesPorId[xId] || [])));
            const xOpcoesPrimeiraPessoa = xOpcoesPorId[xPessoaIds[0]] || [];
            setConsolidacoes(xOpcoes);
            setConsolidacaoAtual(getAtivoPatrimonioInitialOption(xOpcoesPrimeiraPessoa, xOpcoes));

            if (!xOpcoes.length) {
              setSeriesPorId(pAnterior => {
                const xProximo = { ...pAnterior };
                xPessoaIds.forEach(xId => {
                  xProximo[xId] = { ...xProximo[xId], loading: false };
                });
                return xProximo;
              });
            }
          }
        }
      );
    });

    return () => {
      requestCycleRef.current += 1;
    };
  }, [xPessoaIdsKey]);

  useEffect(() => {
    if (!xPessoaIds.length || validators.isNull(consolidacaoAtual)) return undefined;

    const xCycle = requestCycleRef.current;
    let xCurrent = true;
    setSeriesPorId(pAnterior => {
      const xProximo = { ...pAnterior };
      xPessoaIds.forEach(xId => {
        xProximo[xId] = { ...xProximo[xId], loading: true };
      });
      return xProximo;
    });

    xPessoaIds.forEach(xId => {
      services.pessoas.pl(
        {
          data: null,
          data_fim: wDataFim,
          pessoa_id: xId,
          sort: 'data ASC',
          consolidacao_tipo: consolidacaoAtual
        },
        rRes => {
          if (!xCurrent || requestCycleRef.current !== xCycle) return;
          setSeriesPorId(pAnterior => ({
            ...pAnterior,
            [xId]: {
              ...pAnterior[xId],
              data: getAtivoPatrimonioPlData(rRes.data),
              cotistasData: getAtivoPatrimonioCotistasData(rRes.data),
              historicoData: rRes.data || [],
              error: false
            }
          }));
        },
        () => {
          if (!xCurrent || requestCycleRef.current !== xCycle) return;
          setSeriesPorId(pAnterior => ({
            ...pAnterior,
            [xId]: {
              ...pAnterior[xId],
              data: [],
              cotistasData: [],
              historicoData: [],
              error: true
            }
          }));
        },
        () => {
          if (!xCurrent || requestCycleRef.current !== xCycle) return;
          setSeriesPorId(pAnterior => ({
            ...pAnterior,
            [xId]: { ...pAnterior[xId], loading: false }
          }));
        }
      );
    });

    return () => {
      xCurrent = false;
    };
  }, [consolidacaoAtual, xPessoaIdsKey]);

  const xSeries = xPessoaIds.map(xId => ({
    pessoaId: xId,
    pessoa: pessoasPorId[xId] || {},
    nome: pessoasPorId[xId]?.pessoa_fantasia || String(xId),
    data: seriesPorId[xId]?.data || [],
    cotistasData: seriesPorId[xId]?.cotistasData || [],
    historicoData: seriesPorId[xId]?.historicoData || []
  }));
  // .filter(xSerie => !validators.isEmpty(xSerie.data) || !validators.isEmpty(xSerie.cotistasData));
  const xPessoa = pessoasPorId[xPessoaIds[0]] || xSeries[0]?.pessoa;
  const xPatrimonioDescription = getAtivoPatrimonioDescription(xPessoa);
  const xHasCotistas = xSeries.some(xSerie => !validators.isEmpty(xSerie.cotistasData));
  const xMetricOptions = [
    { value: ATIVO_PATRIMONIO_METRIC_TYPE.pl, description: xPatrimonioDescription },
    ...(xHasCotistas
      ? [{ value: ATIVO_PATRIMONIO_METRIC_TYPE.cotistas, description: 'Cotistas' }]
      : [])
  ];
  const xMetricDescription =
    xMetricOptions.find(xItem => xItem.value === metricaAtual)?.description ||
    xPatrimonioDescription;
  const xCurrentMetricRows = xSeries
    .map((xSerie, xIndex) => {
      const xMetricValues = xMetricOptions.map(xMetric => {
        const xCurrentMetricInfo = getAtivoPatrimonioCurrentMetricInfo(
          xSerie.historicoData,
          xMetric.value
        );
        const xCurrentMetricValue = validators.isNull(xCurrentMetricInfo.valor)
          ? null
          : formats.friendlyNumber(xCurrentMetricInfo.valor, 2, false);
        const xCurrentMetricDate = xCurrentMetricInfo.data
          ? formats.formatDateCustom(xCurrentMetricInfo.data, 'DD/MMM/YY')
          : null;
        const xCurrentMetricDateTimestamp = xCurrentMetricInfo.data
          ? dates.toDate(xCurrentMetricInfo.data).getTime()
          : null;

        return {
          ...xMetric,
          currentValue: xCurrentMetricValue,
          currentDate: xCurrentMetricDate,
          dateTimestamp: xCurrentMetricDateTimestamp
        };
      });
      // const xCurrentMetric = xMetricValues.find(
      //   xMetric =>
      //     !validators.isNull(xMetric.currentValue) && !validators.isNull(xMetric.currentDate)
      // );

      const xCurrentMetric = xMetricValues;

      if (!xCurrentMetric) return null;

      const xSortName = isUpMd
        ? xSerie.nome
        : isUpSm
        ? utils.typo.shortname(xSerie.nome, 45)
        : utils.typo.shortname(xSerie.nome, 25);

      return {
        id: `${xSerie.nome}-${xIndex}`,
        nome: xSortName,
        pessoa: xSerie.pessoa,
        metrics: xMetricValues
      };
    })
    .filter(xValue => !validators.isNull(xValue));
  const xLatestMetricsByType = xMetricOptions.reduce((pLatestMetrics, pMetric) => {
    const xLatestMetric = xCurrentMetricRows.reduce((pLatestMetric, pRow) => {
      const xMetric = pRow.metrics.find(xItem => xItem.value === pMetric.value);

      if (
        validators.isNull(xMetric?.dateTimestamp) ||
        (!validators.isNull(pLatestMetric) && xMetric.dateTimestamp <= pLatestMetric.dateTimestamp)
      ) {
        return pLatestMetric;
      }

      return xMetric;
    }, null);

    return { ...pLatestMetrics, [pMetric.value]: xLatestMetric };
  }, {});

  useEffect(() => {
    if (!xHasCotistas && metricaAtual === ATIVO_PATRIMONIO_METRIC_TYPE.cotistas) {
      setMetricaAtual(ATIVO_PATRIMONIO_METRIC_TYPE.pl);
    }
  }, [metricaAtual, xHasCotistas]);

  const xOptions = useMemo(() => {
    const xChartSeries = xSeries
      .map((xSerie, xIndex) => ({
        id: String(xSerie.pessoaId),
        index: xIndex,
        name: xSerie.nome,
        type: 'line',
        dashStyle: wSeriesDashStyles[xIndex % wSeriesDashStyles.length],
        data:
          metricaAtual === ATIVO_PATRIMONIO_METRIC_TYPE.cotistas
            ? xSerie.cotistasData
            : xSerie.data,
        connectNulls: metricaAtual === ATIVO_PATRIMONIO_METRIC_TYPE.cotistas
      }))
      .filter(xSerie => !validators.isEmpty(xSerie.data));
    const xUseLogarithmicScale =
      metricaAtual === ATIVO_PATRIMONIO_METRIC_TYPE.pl &&
      xChartSeries.length > 1 &&
      xChartSeries.every(xSerie =>
        xSerie.data.every(([, pValue]) => validators.isNull(pValue) || pValue > 0)
      );
    const xFormattedChartSeries = xChartSeries.map(xSerie => ({
      ...xSerie,
      ...(!xUseLogarithmicScale && metricaAtual === ATIVO_PATRIMONIO_METRIC_TYPE.pl
        ? { zones: [{ value: 0, color: '#ed442c' }, { color: '#00dfa8' }] }
        : {})
    }));

    return {
      title: { text: '' },
      tooltip: {
        formatter() {
          return `<span style="font-size: 1em"><b>${formats.formatDateCustom(
            this.point.x,
            'MMM/YYYY'
          )}</b></span><br/><span style="color:${this.color}">●</span>${
            this.series.name
          }: <b>${formats.friendlyNumber(this.point.y, 2, false)}</b> (${formats.formatNumber(
            this.point.y,
            2,
            true,
            false
          )})<br/>`;
        }
      },
      yAxis: {
        type: xUseLogarithmicScale ? 'logarithmic' : 'linear',
        title: {
          text:
            metricaAtual === ATIVO_PATRIMONIO_METRIC_TYPE.cotistas
              ? 'Cotistas'
              : `${xPatrimonioDescription} (R$)`
        },
        ...(metricaAtual === ATIVO_PATRIMONIO_METRIC_TYPE.cotistas ? { allowDecimals: false } : {})
      },
      xAxis: {
        type: 'datetime',
        crosshair: true,
        labels: { format: "{value:%b'%y}" }
      },
      series: xFormattedChartSeries
    };
  }, [metricaAtual, xPatrimonioDescription, xSeries]);
  const xConsolidacoes = consolidacoes.map(pId => {
    const xItem = Object.values(CONSOLIDACAO_TIPO).find(pTipo => pTipo.id === pId);
    return { value: pId, label: xItem?.descricao || pId };
  });
  const xAtual =
    xConsolidacoes.find(xItem => xItem.value === consolidacaoAtual) || xConsolidacoes[0];
  const xLoading = xPessoaIds.some(xId => seriesPorId[xId]?.loading);

  const pvSemDado = () => (
    <Tooltip title="Sem dado" arrow>
      <Box component="span" tabIndex={0} sx={{ display: 'inline-flex' }}>
        <Typography component="span" variant="caption" fontWeight="bold">
          -
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Stack flexGrow={1}>
      <Card>
        <CardHeader
          disableTypography
          sx={{
            '& .MuiCardHeader-content': {
              minWidth: 0
            }
          }}
          title={
            <Stack spacing={1}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Stack width={21} flexShrink={0}>
                  <Icon iconName="chart_line" color="primary" size={21} />
                </Stack>
                <Stack>
                  <Typography
                    variant="subtitle1"
                    color="textPrimary"
                    sx={{ fontWeight: 600 }}
                    noWrap>
                    Evolução Patrimonial
                  </Typography>
                </Stack>
              </Stack>
              <Stack spacing={2}>
                <TableContainer sx={{ overflow: 'auto' }}>
                  <Table size="small" aria-label="Resumo do patrimônio" sx={{ width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            width: '1%',
                            whiteSpace: 'nowrap',
                            backgroundColor: pTheme => pTheme.palette.secondary.dark
                          }}
                        />
                        {xMetricOptions.map(xMetric => (
                          <React.Fragment key={xMetric.value}>
                            <TableCell align="right" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                              <Stack alignItems="flex-end">
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  sx={{ fontWeight: 500 }}
                                  noWrap>
                                  {xMetric.description}
                                </Typography>
                                <Typography
                                  data-testid={`patrimonio-resumo-${xMetric.value}-data`}
                                  variant="caption"
                                  color="textSecondary"
                                  sx={{ fontWeight: 500 }}
                                  noWrap>
                                  {xLatestMetricsByType[xMetric.value]?.currentDate}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell aria-hidden sx={{ width: '1%', p: 0 }} />
                          </React.Fragment>
                        ))}
                        <TableCell aria-hidden sx={{ p: 0 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {xCurrentMetricRows.map(xRow => (
                        <TableRow key={xRow.id}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              width: '1%',
                              whiteSpace: 'nowrap',
                              backgroundColor: pTheme => pTheme.palette.secondary.dark
                            }}>
                            <Stack
                              direction="row"
                              gap={1}
                              alignItems="center"
                              sx={{ '& img': { display: 'block' } }}>
                              {xRow?.pessoa?.logo_pessoa_id && (
                                <PessoaImage
                                  logoPessoaId={xRow.pessoa.logo_pessoa_id}
                                  alt={xRow.pessoa.pessoa}
                                  hasLogo={xRow.pessoa.logo_ok}
                                  size={18}
                                />
                              )}
                              <Tooltip title={xRow.nome} arrow>
                                <Typography
                                  data-testid="patrimonio-resumo-nome"
                                  variant="body2"
                                  color="textPrimary"
                                  sx={{
                                    fontWeight: 500
                                  }}
                                  noWrap>
                                  {xRow.nome}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          {xRow.metrics.map(xMetric => {
                            const xIsOlderMetric =
                              !validators.isNull(xMetric.dateTimestamp) &&
                              xMetric.dateTimestamp <
                                xLatestMetricsByType[xMetric.value]?.dateTimestamp;

                            return (
                              <React.Fragment key={xMetric.value}>
                                <TableCell align="right" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                                  {xMetric.currentValue ? (
                                    <Typography
                                      data-testid={`patrimonio-resumo-${xMetric.value}-value`}
                                      variant="body2"
                                      color="textPrimary"
                                      sx={{ fontWeight: 500 }}
                                      noWrap>
                                      {xMetric.currentValue}
                                    </Typography>
                                  ) : (
                                    pvSemDado()
                                  )}
                                </TableCell>
                                <TableCell
                                  aria-label={xIsOlderMetric ? 'Data do valor' : undefined}
                                  sx={{ width: '1%', pl: 0, pr: 1, whiteSpace: 'nowrap' }}>
                                  {xIsOlderMetric ? (
                                    <Tooltip
                                      title={`Valor referente à data: ${xMetric.currentDate}`}
                                      arrow>
                                      <Box sx={{ width: '16px', height: '16px' }}>
                                        <Icon size={16} color="primary" iconName="information" />
                                      </Box>
                                    </Tooltip>
                                  ) : null}
                                </TableCell>
                              </React.Fragment>
                            );
                          })}
                          <TableCell aria-hidden sx={{ p: 0 }} />
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Stack>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} p={2}>
            {xMetricOptions.length > 1 ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={pEvent => setAnchorElMetrica(pEvent.currentTarget)}>
                  {xMetricDescription}
                </Button>
                <Menu
                  anchorEl={anchorElMetrica}
                  open={Boolean(anchorElMetrica)}
                  onClose={() => setAnchorElMetrica(null)}>
                  {xMetricOptions.map(xItem => (
                    <MenuItem
                      key={xItem.value}
                      onClick={() => {
                        setMetricaAtual(xItem.value);
                        setAnchorElMetrica(null);
                      }}>
                      {xItem.description}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : null}
            {xConsolidacoes.length > 1 ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={pEvent => setAnchorElConsolidacao(pEvent.currentTarget)}>
                  {xAtual?.label}
                </Button>
                <Menu
                  anchorEl={anchorElConsolidacao}
                  open={Boolean(anchorElConsolidacao)}
                  onClose={() => setAnchorElConsolidacao(null)}>
                  {xConsolidacoes.map(xItem => (
                    <MenuItem
                      key={xItem.value}
                      onClick={() => {
                        setConsolidacaoAtual(xItem.value);
                        setAnchorElConsolidacao(null);
                      }}>
                      {xItem.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : null}
          </Stack>
          <HighchartsReact
            key={`ativo-patrimonio-${metricaAtual}`}
            options={xOptions}
            isLoading={xLoading}
          />
        </CardContent>
      </Card>
    </Stack>
  );
});

export default AtivoPatrimonio;
